import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useListing } from '../hooks/useListings.js';
import { useMe } from '../hooks/useAuth.js';
import { TRAILER_TYPE_MAP } from '../config/trailerTypes.js';
import { BookingWidget } from '../components/BookingWidget.js';
import { ListingLocationMap } from '../components/ListingLocationMap.js';
import { useUserReviews } from '../hooks/useReviews.js';

export function ListingDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useListing(slug);
  const me = useMe();
  const [activePhoto, setActivePhoto] = useState(0);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const ownerReviews = useUserReviews(data?.listing?.ownerId);

  if (isLoading) {
    return <div className="mx-auto max-w-6xl px-4 py-16 text-navy-500">Loading…</div>;
  }
  if (!data?.listing) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-16">
        <p className="text-navy-600 mb-4">We couldn't find that listing.</p>
        <Link to="/search" className="btn-primary">
          Browse trailers
        </Link>
      </div>
    );
  }
  const l = data.listing;
  const trailerInfo = TRAILER_TYPE_MAP[l.trailerType];
  const isOwner = me.data?.user?.id === l.ownerId;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 pb-32 lg:pb-8">
      <nav className="text-sm text-navy-500 mb-4">
        <Link to="/search" className="hover:text-navy">
          Trailers
        </Link>
        <span className="mx-1.5">›</span>
        <Link to={`/search?type=${l.trailerType}`} className="hover:text-navy">
          {trailerInfo?.label}
        </Link>
        <span className="mx-1.5">›</span>
        <span>
          {l.city}, {l.province}
        </span>
      </nav>

      <h1 className="text-3xl md:text-4xl font-bold text-navy mb-2">{l.title}</h1>
      <div className="flex flex-wrap items-center gap-3 text-navy-600 mb-6">
        <span>
          {trailerInfo?.emoji} {trailerInfo?.label}
        </span>
        <span>·</span>
        <span>
          {l.city}, {l.province}
        </span>
        {l.instantBookable && (
          <>
            <span>·</span>
            <span className="text-orange font-semibold">Instant book</span>
          </>
        )}
        {ownerReviews.data && ownerReviews.data.count > 0 && (
          <>
            <span>·</span>
            <span className="text-orange">★</span>
            <span className="font-semibold text-navy">
              {ownerReviews.data.average?.toFixed(1)}
            </span>
            <span>
              ({ownerReviews.data.count} review{ownerReviews.data.count === 1 ? '' : 's'})
            </span>
          </>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <PhotoGallery photos={l.photos} active={activePhoto} onSelect={setActivePhoto} />

          <section>
            <h2 className="text-xl font-semibold text-navy mb-3">About this trailer</h2>
            <p className="text-navy-700 whitespace-pre-line leading-relaxed">
              {l.description || 'No description provided.'}
            </p>
          </section>

          <section className="card p-6">
            <h2 className="text-xl font-semibold text-navy mb-4">Specs</h2>
            <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              <Spec label="Length" value={l.lengthFt ? `${l.lengthFt} ft` : '—'} />
              <Spec label="Width" value={l.widthFt ? `${l.widthFt} ft` : '—'} />
              <Spec
                label="Weight capacity"
                value={l.weightCapacityLb ? `${l.weightCapacityLb.toLocaleString()} lb` : '—'}
              />
              <Spec label="Year built" value={l.yearBuilt?.toString() ?? '—'} />
              <Spec label="Hitch size" value={l.hitchSize ?? '—'} />
              <Spec label="Brake type" value={l.brakeType ?? '—'} />
              <Spec label="Trailer type" value={trailerInfo?.label ?? l.trailerType} />
              <Spec label="Deposit" value={`$${(l.depositCents / 100).toFixed(0)}`} />
            </dl>
          </section>

          {l.rules && (
            <section className="card p-6">
              <h2 className="text-xl font-semibold text-navy mb-3">Rules</h2>
              <p className="text-navy-700 whitespace-pre-line">{l.rules}</p>
            </section>
          )}

          {ownerReviews.data && ownerReviews.data.count > 0 && (
            <section className="card p-6">
              <h2 className="text-xl font-semibold text-navy mb-3">
                {ownerReviews.data.average?.toFixed(1)} ★ · {ownerReviews.data.count}{' '}
                review{ownerReviews.data.count === 1 ? '' : 's'}
              </h2>
              <div className="space-y-4">
                {ownerReviews.data.reviews.slice(0, 5).map((r) => (
                  <div key={r.id} className="border-b border-navy-50 pb-3 last:border-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-orange text-sm">
                        {'★'.repeat(r.rating)}
                        {'☆'.repeat(5 - r.rating)}
                      </span>
                      <span className="text-sm font-medium text-navy">
                        {[r.reviewerFirst, r.reviewerLast].filter(Boolean).join(' ') ||
                          'Renter'}
                      </span>
                    </div>
                    {r.body && (
                      <p className="text-sm text-navy-700 whitespace-pre-line">{r.body}</p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="card p-6">
            <h2 className="text-xl font-semibold text-navy mb-3">Pickup location</h2>
            <p className="text-navy-700">
              <strong>
                {l.city}, {l.province}
              </strong>
              {l.addressVisible && l.addressLine && (
                <span className="text-navy-700"> · {l.addressLine}</span>
              )}
            </p>
            {(() => {
              const mapLat = l.addressVisible ? l.lat : l.fuzzedLat;
              const mapLng = l.addressVisible ? l.lng : l.fuzzedLng;
              if (typeof mapLat !== 'number' || typeof mapLng !== 'number') return null;
              return (
                <div className="mt-4">
                  <ListingLocationMap
                    lat={mapLat}
                    lng={mapLng}
                    precise={Boolean(l.addressVisible)}
                  />
                  <p className="text-xs text-navy-500 mt-2 leading-snug">
                    {(() => {
                      switch (l.viewerRelationship) {
                        case 'owner':
                          return 'You\'re the owner — this is your exact address. Renters only see a general area on the map until they pay.';
                        case 'admin':
                          return 'Admin view — exact address visible. Public visitors see a fuzzed area.';
                        case 'paid_booking':
                          return 'Exact pickup location — head straight here.';
                        default:
                          return 'General pickup area. The exact street address is shared with the renter once payment lands.';
                      }
                    })()}
                  </p>
                </div>
              );
            })()}
            {!l.addressVisible && (
              <p className="text-sm text-navy-500 mt-3">
                Exact address shared with the renter once a booking is confirmed.
              </p>
            )}
          </section>
        </div>

        <aside className="hidden lg:block lg:col-span-1">
          <BookingWidget
            listing={l}
            isOwner={isOwner}
            onEdit={() => navigate(`/listings/${l.id}/edit`)}
          />
        </aside>
      </div>

      {/* Mobile sticky bottom CTA + sheet */}
      {!isOwner && (
        <div className="lg:hidden fixed bottom-16 inset-x-0 z-30 bg-white border-t border-navy-100 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] flex items-center gap-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          <div>
            <div className="text-lg font-bold text-navy leading-tight">
              ${(l.dailyRateCents / 100).toFixed(0)}
              <span className="text-sm font-normal text-navy-500"> / day</span>
            </div>
            <div className="text-xs text-navy-500">
              {l.instantBookable ? 'Instant book' : 'Request to book'} · ${(l.depositCents / 100).toFixed(0)} deposit
            </div>
          </div>
          <button
            type="button"
            className="btn-primary ml-auto px-6"
            onClick={() => setMobileSheetOpen(true)}
          >
            {l.instantBookable ? 'Book' : 'Request'}
          </button>
        </div>
      )}

      {isOwner && (
        <div className="lg:hidden fixed bottom-16 inset-x-0 z-30 bg-white border-t border-navy-100 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
          <button
            type="button"
            className="btn-secondary w-full"
            onClick={() => navigate(`/listings/${l.id}/edit`)}
          >
            Edit your listing
          </button>
        </div>
      )}

      {mobileSheetOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end">
          <div
            className="absolute inset-0 bg-navy/40"
            onClick={() => setMobileSheetOpen(false)}
            aria-hidden="true"
          />
          <div className="relative w-full bg-white rounded-t-2xl max-h-[90vh] overflow-y-auto animate-slide-up shadow-2xl">
            <div className="sticky top-0 bg-white flex justify-center py-2 border-b border-navy-50">
              <div className="w-10 h-1 bg-navy-100 rounded-full" />
            </div>
            <div className="p-4">
              <BookingWidget
                listing={l}
                isOwner={false}
                onClose={() => setMobileSheetOpen(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PhotoGallery({
  photos,
  active,
  onSelect,
}: {
  photos: { url: string; thumbnailUrl: string }[];
  active: number;
  onSelect: (i: number) => void;
}) {
  if (photos.length === 0) {
    return (
      <div className="aspect-[16/10] rounded-xl bg-navy-50 flex items-center justify-center text-6xl">
        🛻
      </div>
    );
  }
  const current = photos[active] ?? photos[0];
  return (
    <div>
      <div className="aspect-[16/10] rounded-xl overflow-hidden bg-navy-50">
        <img
          src={current.url}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      {photos.length > 1 && (
        <div className="mt-3 grid grid-cols-5 gap-2">
          {photos.slice(0, 5).map((p, i) => (
            <button
              key={p.url}
              type="button"
              onClick={() => onSelect(i)}
              className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                i === active ? 'border-orange' : 'border-transparent opacity-70 hover:opacity-100'
              }`}
            >
              <img
                src={p.thumbnailUrl}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Spec({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between border-b border-navy-50 pb-2">
      <dt className="text-navy-500">{label}</dt>
      <dd className="text-navy font-medium">{value}</dd>
    </div>
  );
}

